import type { IncludeExcludeState } from '@pulsepointinc/bi-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type SuggestionItem, transformStringExpressionToIncludeExclude } from '../../../../../../../helpers';
import {
  getExpressionListFromString,
  getModeFromFilterState,
} from '../../../../../../../helpers/groupIncludeExcludeFilter';
import { useNpiListsMetadataApi } from '../../../../../../../hooks/useWorkspaces';
import type { ExpressionType } from '../../../../../../../stores/filters';
import { useUserData } from '../../../../hooks/useUserData';

interface AliasLookupParams {
  expression: string;
  advertiserID?: string;
  suggestions: Record<string, SuggestionItem[]>;
}

export const useAliasedFilterState = ({ expression, advertiserID, suggestions }: AliasLookupParams) => {
  const {
    userData: { acctId },
  } = useUserData();

  const aliasMap = useRef<Map<string, string>>(new Map());
  const [filterState, setFilterState] = useState<IncludeExcludeState>({});

  useEffect(() => {
    const transformedValue = expression ? transformStringExpressionToIncludeExclude(expression) : {};

    setFilterState((currentStateValue) => ({
      ...transformedValue,
      ...currentStateValue,
    }));
  }, [expression]);

  const parsedExpression = useMemo(() => {
    if (!expression) return [];
    return getExpressionListFromString(expression);
  }, [expression]);

  const { listMetadata: initialSelectedItems, isListMetadataLoading } = useNpiListsMetadataApi({
    accountID: Number(acctId),
    advertiserID: Number(advertiserID),
    lists: parsedExpression,
    enabled: !!parsedExpression.length,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!initialSelectedItems.length) return;
    initialSelectedItems.forEach((item) => {
      if (item.listAlias) {
        aliasMap.current.set(item.listName, item.listAlias);
      }
    });

    setFilterState((currentState) => {
      const newState = { ...currentState };
      initialSelectedItems.forEach((item) => {
        const itemToReplace = newState[item.listAlias];
        if (itemToReplace) {
          newState[item.listName] = itemToReplace;
          delete newState[item.listAlias];
        }
      });
      return newState;
    });
  }, [initialSelectedItems]);

  useEffect(() => {
    if (!Object.keys(suggestions)) return;
    const suggestionList = Object.values(suggestions).flat();
    suggestionList.forEach((item) => {
      if (item.listAlias) {
        aliasMap.current.set(item.listName, item.listAlias);
      }
    });

    setFilterState((currentState) => {
      const newState = { ...currentState };
      suggestionList.forEach((item) => {
        if (!newState[item.listName]) {
          newState[item.listName] = {
            check: false,
            block: false,
          };
        }
      });
      return newState;
    });
  }, [suggestions]);

  const mode = useMemo(() => getModeFromFilterState(filterState), [filterState]);

  const handleChange = useCallback(
    (newState: IncludeExcludeState) => {
      const mode = getModeFromFilterState(newState);
      const expressionType: ExpressionType = mode === 'block' ? 'block' : 'target';

      const expressionParts: string[] = [];

      for (const [listName, state] of Object.entries(newState)) {
        if (!state.check && !state.block) continue;
        const alias = aliasMap.current.get(listName);
        if (!alias) return listName;
        expressionParts.push(state.block ? `-${alias}` : alias);
      }

      const expression = expressionParts.join(',');
      setFilterState(newState);

      return { mode, expressionType, expression };
    },
    [aliasMap, setFilterState],
  );

  return {
    aliasMap: aliasMap.current,
    filterState,
    setFilterState,
    handleChange,
    isLoading: isListMetadataLoading,
    mode,
  };
};
